import { useEffect, useRef, useState } from "react"
import { ChevronDown, ChevronUp, Send } from "lucide-react"
import { useAuth } from "../../context/AuthContext"
import {
  addCommunityComment,
  fetchCommunityComments,
  subscribeToNewComments,
  COMMENTS_PAGE_SIZE,
} from "../../community/community-service"
import { formatTimeAgo, getAvatarInitialColor } from "../../community/community-ui-utils"

export default function CommunityPostComments({ post, onCommentAdded }) {
  const { currentUser, userProfile } = useAuth()

  const [open, setOpen] = useState(false)
  const [comments, setComments] = useState([])
  const [loadingComments, setLoadingComments] = useState(false)
  const [loadingMoreComments, setLoadingMoreComments] = useState(false)
  const [hasMoreComments, setHasMoreComments] = useState(true)
  const [replyText, setReplyText] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const lastCommentDocRef = useRef(null)
  const unsubscribeNewCommentsRef = useRef(null)
  const suppressNextAutoScrollRef = useRef(false)
  const listEndRef = useRef(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoadingComments(true)
    ;(async () => {
      try {
        const { comments: firstPage, lastDoc } = await fetchCommunityComments(
          post.id,
          COMMENTS_PAGE_SIZE,
          null
        )
        if (cancelled) return
        setComments(firstPage)
        lastCommentDocRef.current = lastDoc
        setHasMoreComments(firstPage.length === COMMENTS_PAGE_SIZE)
      } catch (err) {
        console.error("Failed to load comments:", err)
      } finally {
        if (!cancelled) setLoadingComments(false)
      }
    })()
    return () => {
      cancelled = true
      if (unsubscribeNewCommentsRef.current) {
        unsubscribeNewCommentsRef.current()
        unsubscribeNewCommentsRef.current = null
      }
      lastCommentDocRef.current = null
      setComments([])
      setHasMoreComments(true)
    }
  }, [post.id, open])

  useEffect(() => {
    if (!open || loadingComments || comments.length === 0) return
    if (unsubscribeNewCommentsRef.current) {
      unsubscribeNewCommentsRef.current()
      unsubscribeNewCommentsRef.current = null
    }
    const newestMs = comments[comments.length - 1]?.createdAt ?? null
    unsubscribeNewCommentsRef.current = subscribeToNewComments(
      post.id,
      newestMs,
      (newComments) => {
        setComments((prev) => {
          const existing = new Set(prev.map((c) => c.id))
          const toAppend = newComments.filter((c) => !existing.has(c.id))
          return toAppend.length ? [...prev, ...toAppend] : prev
        })
      }
    )
    return () => {
      if (unsubscribeNewCommentsRef.current) {
        unsubscribeNewCommentsRef.current()
        unsubscribeNewCommentsRef.current = null
      }
    }
  }, [open, loadingComments, comments.length, post.id, comments])

  useEffect(() => {
    if (!open || comments.length === 0) return
    if (suppressNextAutoScrollRef.current) {
      suppressNextAutoScrollRef.current = false
      return
    }
    listEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [open, comments.length])

  const loadMoreComments = async () => {
    if (loadingMoreComments || !hasMoreComments) return
    const lastDoc = lastCommentDocRef.current
    if (!lastDoc) return
    suppressNextAutoScrollRef.current = true
    setLoadingMoreComments(true)
    try {
      const { comments: nextPage, lastDoc: nextLastDoc } = await fetchCommunityComments(
        post.id,
        COMMENTS_PAGE_SIZE,
        lastDoc
      )
      setComments((prev) => [...nextPage, ...prev])
      lastCommentDocRef.current = nextLastDoc
      setHasMoreComments(nextPage.length === COMMENTS_PAGE_SIZE)
    } catch (err) {
      console.error("Failed to load older comments:", err)
    } finally {
      setLoadingMoreComments(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const trimmed = replyText.trim()
    if (!trimmed || !currentUser?.uid || submitting) return

    setSubmitting(true)
    try {
      const username =
        userProfile?.username ||
        userProfile?.full_name ||
        currentUser.email?.split("@")[0] ||
        "Anonymous"

      await addCommunityComment(post.id, {
        uid: currentUser.uid,
        username,
        avatar: userProfile?.profile_picture || "",
        message: trimmed,
      })

      onCommentAdded?.(post.id)
      setReplyText("")
    } catch (err) {
      console.error("Failed to add comment:", err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mt-3 border-t border-gray-800/60 pt-3">
      <button
        type="button"
        className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        <span>Comments ({post.replyCount})</span>
      </button>

      {open ? (
        <div className="mt-3 space-y-3">
          <div className="max-h-48 space-y-2 overflow-y-auto pr-1">
            {loadingComments ? (
              <p className="text-sm text-gray-400">Loading comments...</p>
            ) : (
              <>
                {hasMoreComments ? (
                  <div className="flex justify-center pb-1">
                    <button
                      type="button"
                      onClick={loadMoreComments}
                      disabled={loadingMoreComments}
                      className="rounded-full border border-gray-700 bg-gray-800/50 px-3 py-1 text-[11px] font-medium text-gray-200 hover:bg-gray-700/60 disabled:opacity-50"
                    >
                      {loadingMoreComments ? "Loading..." : "Load more"}
                    </button>
                  </div>
                ) : comments.length > 0 ? (
                  <div className="flex justify-center pb-1">
                    <span className="text-[11px] text-gray-500">No older comments</span>
                  </div>
                ) : null}

                {comments.length === 0 ? (
              <p className="text-sm text-gray-400">No comments yet.</p>
            ) : (
              comments.map((c) => (
                <div key={c.id} className="flex gap-2 text-sm">
                  {c.avatar ? (
                    <img
                      src={c.avatar}
                      alt=""
                      className="h-7 w-7 shrink-0 rounded-full object-cover border border-gray-800/60"
                    />
                  ) : (
                    <div
                      className={[
                        "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-800/60 text-xs font-semibold",
                        getAvatarInitialColor(c.uid || c.username || "?"),
                      ].join(" ")}
                    >
                      {(c.username || "?")[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="font-medium text-white/90">{c.username}</span>
                      <span className="text-xs text-gray-500">{formatTimeAgo(c.createdAt)}</span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap break-words text-gray-300">
                      {c.message}
                    </p>
                  </div>
                </div>
              ))
            )}
              </>
            )}
            <div ref={listEndRef} />
          </div>

          {currentUser ? (
            <form onSubmit={handleSubmit} className="flex gap-2">
              <input
                placeholder="Write a comment..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                className="flex-1 bg-gray-900/30 border border-gray-800/60 rounded-md px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/40"
                disabled={submitting}
              />
              <button
                type="submit"
                className="h-10 w-10 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center disabled:opacity-50 transition-colors"
                disabled={!replyText.trim() || submitting}
                aria-label="Send comment"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

